/**
 * AccountIdentityFields — the account identity (the two bucket dims) at the top of the configure
 * page (ACT2-1031). It replaces the old step 2, which asked for the same two values on a screen of
 * its own, beside a list of synced-destination buckets.
 *
 * Each field is **type or pick**: the shared `ComboboxField` in its free-text mode, with the
 * synced-destination buckets offered as suggestions.
 *
 *  - **Picking** a suggestion selects that bucket, with its representative destination, so the form
 *    below prefills and links to the destination exactly as picking a bucket row used to.
 *  - **Typing** coordinates no destination covers mints a synthetic bucket, as the manual panel did.
 *
 * The bucket is committed on blur, or as soon as the value matches a suggestion — never per
 * keystroke, because the form below remounts on every new bucket to re-run its prefill.
 *
 * The dims handed up are read back out of the derived account key rather than taken from the raw
 * input, so they carry the type-aware normalization the server applies when it re-derives the key
 * (Postgres/Snowflake/Redshift lowercase; BigQuery/Databricks keep case).
 *
 * A connected bucket locks only its own coordinates: its **database** suggestion is disabled with a
 * tooltip saying so, while its host stays pickable, because another database on the same host is a
 * new account (as on the old step 2, which disabled the bucket row, never the host). Typing a connected
 * bucket's coordinates by hand is still possible, which is why the duplicate hint below the fields stays;
 * it is a **hint only** — the server's `CONFLICT` on create is the actual guard.
 */
import { deriveAccountKeyFromDims, deriveConnectionKey } from '@server/warehouse-keys';
import * as React from 'react';

import { Callout } from '@/_shared/components/ui/callout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { ComboboxField, type ComboboxOption } from '@/_shared/components/ui/combobox';
import { Label } from '@/_shared/components/ui/label';

import type { WarehouseBucket } from '../useWarehouseAccounts';
import { manualEntryFields, type WarehouseTypeMeta } from '../warehouseTypes';

interface AccountIdentityFieldsProps {
  type: WarehouseTypeMeta;
  /** Synced-destination buckets of this type — the suggestions. */
  buckets: WarehouseBucket[];
  /** The bucket currently selected, if any — seeds the fields when the page remounts. */
  bucket: WarehouseBucket | null;
  /** Connection keys already stored, so the fields can warn before the server says CONFLICT. */
  existingConnectionKeys: string[];
  onSelect: (bucket: WarehouseBucket | null) => void;
  /** The drawer's sheet content: the suggestion popups portal there so they stay clickable. */
  portalContainer?: React.RefObject<HTMLElement | null>;
}

function syntheticBucket(type: WarehouseTypeMeta, accountKey: string): WarehouseBucket {
  const [, hostDim = '', dbDim = ''] = accountKey.split('|');
  return {
    accountKey,
    type: type.key,
    hostDim,
    dbDim,
    availableWarehouses: [],
    destinationCount: 0,
    bucketWideConnected: false,
    schemaConnections: [],
    unmappedCount: 0,
  };
}

const ALREADY_CONNECTED = 'Already connected in Lookout';

function destinations(count: number): string {
  return count === 1 ? '1 destination' : `${count} destinations`;
}

/**
 * One suggestion per distinct dim value. Two buckets can share a host (one per database), so the
 * host list folds them. With `lockConnected`, a value is disabled when every bucket behind it is
 * already connected; only a field that completes the account key may lock.
 */
function suggestions(
  buckets: WarehouseBucket[],
  dimOf: (b: WarehouseBucket) => string,
  metaOf: (group: WarehouseBucket[]) => string,
  lockConnected: boolean,
): ComboboxOption[] {
  const groups = new Map<string, WarehouseBucket[]>();
  for (const b of buckets) {
    const value = dimOf(b);
    if (value === '') continue;
    groups.set(value, [...(groups.get(value) ?? []), b]);
  }
  return [...groups].map(([value, group]) => {
    const connected = lockConnected && group.every((b) => b.bucketWideConnected);
    return {
      value,
      label: value,
      meta: metaOf(group),
      ...(connected ? { disabled: true, tooltip: ALREADY_CONNECTED } : {}),
    };
  });
}

const sameDim = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

export function AccountIdentityFields({
  type,
  buckets,
  bucket,
  existingConnectionKeys,
  onSelect,
  portalContainer,
}: AccountIdentityFieldsProps): React.JSX.Element {
  const fields = manualEntryFields(type.key);
  // BigQuery collapses to a single field that feeds both dims.
  const singleDim = fields.length === 1 && fields[0]?.dim === 'both';
  const [hostInput, setHostInput] = React.useState(bucket?.hostDim ?? '');
  const [dbInput, setDbInput] = React.useState(bucket?.dbDim ?? '');
  const titleId = React.useId();

  const keyFor = (host: string, db: string): string | null =>
    deriveAccountKeyFromDims(type.key, host, singleDim ? host : db);

  const commit = (host: string, db: string): void => {
    const accountKey = keyFor(host, db);
    if (accountKey === (bucket?.accountKey ?? null)) return;
    if (accountKey === null) {
      onSelect(null);
      return;
    }
    onSelect(buckets.find((b) => b.accountKey === accountKey) ?? syntheticBucket(type, accountKey));
  };

  const draftKey = keyFor(hostInput, dbInput);
  const draftBucket = draftKey === null ? undefined : buckets.find((b) => b.accountKey === draftKey);
  const duplicate =
    draftKey !== null &&
    (existingConnectionKeys.includes(deriveConnectionKey(draftKey)) || draftBucket?.bucketWideConnected === true);

  // The db suggestions narrow to the typed host once it names one; before that, every bucket's.
  const hostMatches = buckets.filter((b) => sameDim(b.hostDim, hostInput));
  const dbSuggestions = hostMatches.length > 0 ? hostMatches : buckets;

  const hostOptions = suggestions(
    buckets,
    (b) => b.hostDim,
    (group) =>
      singleDim
        ? destinations(group.reduce((n, b) => n + b.destinationCount, 0))
        : group.length === 1 && group[0]
          ? `${group[0].dbDim} · ${group[0].bucketWideConnected ? 'connected' : destinations(group[0].destinationCount)}`
          : `${group.length} databases`,
    // BigQuery's one field is the whole key, so it locks like a database field.
    singleDim,
  );
  const dbOptions = suggestions(
    dbSuggestions,
    (b) => b.dbDim,
    (group) => destinations(group.reduce((n, b) => n + b.destinationCount, 0)),
    true,
  );

  const onHostChange = (value: string): void => {
    setHostInput(value);
    const matches = buckets.filter((b) => sameDim(b.hostDim, value));
    if (matches.length === 0) return;
    // A host that names exactly one bucket names its database too: picking it selects the bucket.
    // Only into an empty database field — never over one the admin already typed — and never a
    // connected one: picking its host is how the admin adds another database on it.
    if (matches.length === 1 && matches[0] && !matches[0].bucketWideConnected && dbInput.trim() === '') {
      setDbInput(matches[0].dbDim);
      commit(value, matches[0].dbDim);
      return;
    }
    commit(value, dbInput);
  };

  const onDbChange = (value: string): void => {
    setDbInput(value);
    if (dbSuggestions.some((b) => sameDim(b.dbDim, value))) commit(hostInput, value);
  };

  return (
    <Card role="group" aria-labelledby={titleId} className="gap-4 py-4">
      <CardHeader className="px-4">
        <CardTitle id={titleId} className="text-sm font-medium">
          Account
        </CardTitle>
        {buckets.length > 0 && (
          <CardDescription className="text-xs">
            Pick one of your synced {type.label} destinations, or type the details of any {type.label} account.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="grid gap-3 px-4">
        {fields.map((field) => {
          const isDb = field.dim === 'db';
          const id = `identity-${field.dim}`;
          const options = isDb ? dbOptions : hostOptions;
          return (
            <div key={field.dim} className="grid gap-1.5">
              <Label htmlFor={id}>
                {field.label}
                {field.optional ? <span className="text-xs font-normal text-grey-400">(optional)</span> : null}
              </Label>
              <ComboboxField
                mode="single"
                allowCustomValue
                id={id}
                aria-label={field.label}
                options={options}
                value={isDb ? dbInput : hostInput}
                onValueChange={isDb ? onDbChange : onHostChange}
                onBlur={() => commit(hostInput, dbInput)}
                placeholder={field.placeholder}
                size="lg"
                className="w-full"
                portalContainer={portalContainer}
              />
            </div>
          );
        })}
        {duplicate && (
          <Callout variant="warning" role="status">
            A connection already exists for this account. Delete it first.
          </Callout>
        )}
      </CardContent>
    </Card>
  );
}
